import { Box, CircularProgress, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { AddClassPostCommentInput } from "@outschool/ui-components-classroom";
import { ErrorMessage } from "@outschool/ui-components-shared";
import React, { useEffect, useRef } from "react";

import { useAssignmentPublicCommentActions } from "../../hooks/useAssignmentPublicCommentActions";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerAssignmentPublicCommentsQuery } from "../../queries/LearnerAssignmentPublicCommentsQuery";
import { ClassDiscussionComment } from "../ClassDiscussion";
import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadViewLayout from "./ThreadViewLayout";

interface AssignmentPublicThreadDetailViewProps {
  assignmentClassPostUid: string;
  postTitle: string | null;
  onBack: () => void;
  onClose: () => void;
  onCommentsChanged: () => void;
}

export default function AssignmentPublicThreadDetailView({
  assignmentClassPostUid,
  postTitle,
  onBack,
  onClose,
  onCommentsChanged,
}: AssignmentPublicThreadDetailViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\PublicThreadDetailView",
  );
  const { section, leader } = useMessageTeacherDrawerContext();
  const currentLearner = useCurrentLearner()!;
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, loading, refetch } = useLearnerAssignmentPublicCommentsQuery({
    sectionUid: section.uid,
    assignmentClassPostUid,
  });
  const assignmentClassPost = data?.assignmentClassPost ?? null;
  const comments = assignmentClassPost?.publicComments ?? [];
  const usersCanComment = data?.classroom?.usersCanComment === true;

  const { error, submitComment, removeComment } =
    useAssignmentPublicCommentActions({
      assignmentClassPostUid,
      onChanged: async () => {
        await refetch();
        onCommentsChanged();
      },
    });

  const messageCount = comments.reduce(
    (count, comment) => count + 1 + comment.replies.length,
    0,
  );
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [messageCount]);

  const trackingMetadata = {
    userUid: currentLearner.parent?.uid,
    learnerUid: currentLearner.uid,
    postUid: assignmentClassPostUid,
  };

  const renderComment = (
    comment:
      | (typeof comments)[number]
      | (typeof comments)[number]["replies"][number],
  ) => {
    const isSender = comment.sender.uid === currentLearner.uid;
    return (
      <ClassDiscussionComment
        key={comment.uid}
        comment={comment}
        isEducator={comment.sender.uid === leader?.uid}
        isSender={isSender}
        timeZone={currentLearner.timeZone}
        onDelete={isSender ? () => removeComment(comment.uid) : undefined}
        trackingMetadata={{ commentUid: comment.uid, ...trackingMetadata }}
      />
    );
  };

  return (
    <ThreadViewLayout
      postTitle={postTitle ?? t("Class discussion")}
      postSubtitle={t("Class discussion · Visible to everyone in class")}
      blockUid={assignmentClassPost ? assignmentClassPostUid : undefined}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollRef}
        data-test-id="AssignmentPublicThreadDetailView"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          p: 16,
        }}
      >
        {loading && !assignmentClassPost ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 16 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !assignmentClassPost ? (
          <Typography variant="body2" color="text.secondary">
            {t("This discussion is no longer available.")}
          </Typography>
        ) : (
          comments.map(comment => (
            <Box
              key={comment.uid}
              sx={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              {renderComment(comment)}
              {comment.replies.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    paddingLeft: 44,
                  }}
                >
                  {comment.replies.map(renderComment)}
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>
      {assignmentClassPost && usersCanComment && (
        <Box sx={{ px: 16, pb: 16 }}>
          <ErrorMessage value={error} />
          <AddClassPostCommentInput
            compact
            placeholder={t("Add to the discussion")}
            onSubmit={submitComment}
          />
        </Box>
      )}
    </ThreadViewLayout>
  );
}
